/**
 * Client-side cleanup for sign-out. Whether the user is signed out is the
 * session cookie's business — see `explicit_logout` on `/auth/me`. This module
 * only drops the data a shared device must not leak to the next user.
 */
import { SELECTED_LANGUAGE_KEY } from '../i18n/language-detection';

/**
 * Preferences, not session state — a logout must not reset language, theme, or
 * the wake-word opt-in. Literal keys owned elsewhere: `app-settings` is
 * `SETTINGS_STORAGE_KEY` in `@nexus/ari-lib` and `ari-hey-ari` lives in
 * `apps/ari`'s settings slice — neither may be imported from here. Device
 * selections (`ari-selected-mic-id` etc.) deliberately reset: shared hardware.
 */
const SURVIVING_KEYS = [SELECTED_LANGUAGE_KEY, 'app-settings', 'ari-hey-ari'] as const;

/**
 * `ariCloud.workspaceSwitchBlocked` explains a refused workspace switch (CLOUD-715).
 * it needs to survive, otherwise the modal that explains what happened won't be seen
 */
const SURVIVING_SESSION_KEYS = ['ariCloud.workspaceSwitchBlocked'] as const;

/** localStorage throws in some sandboxed/partitioned browser contexts. */
function localStore(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch (err) {
    console.warn('[@nexus/supabase] localStorage is unavailable', err);
    return null;
  }
}

/** sessionStorage throws in the same contexts localStorage does. */
function sessionStore(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch (err) {
    console.warn('[@nexus/supabase] sessionStorage is unavailable', err);
    return null;
  }
}

/**
 * Clears both stores, then restores the surviving keys.
 * Must run synchronously before the logout navigation tears the page down.
 */
export function clearClientStorageForLogout(): void {
  const local = localStore();
  try {
    const survivors = SURVIVING_KEYS.map((key) => [key, local?.getItem(key) ?? null] as const);
    local?.clear();
    for (const [key, value] of survivors) {
      if (value !== null) local?.setItem(key, value);
    }
  } catch (err) {
    console.warn('[@nexus/supabase] Could not reset localStorage on logout', err);
  }

  const session = sessionStore();
  try {
    const survivors = SURVIVING_SESSION_KEYS.map((key) => [key, session?.getItem(key) ?? null] as const);
    session?.clear();
    for (const [key, value] of survivors) {
      if (value !== null) session?.setItem(key, value);
    }
  } catch (err) {
    console.warn('[@nexus/supabase] Could not clear sessionStorage on logout', err);
  }
}
